/**
 * Native official-draw field names per lottery source.
 *
 * Storage must match the active house style:
 * - Florida → Pick 3 / Pick 4
 * - New York → Numbers / Win 4
 * - Georgia → Cash 3 / Cash 4
 *
 * Display/settlement may resolve any alias into a shared 3+4 reading.
 */

export type LotteryStyleSource = 'florida' | 'newyork' | 'georgia' | string;

export type OfficialThreeField = 'pick3' | 'numbers' | 'cash3';
export type OfficialFourField = 'pick4' | 'win4' | 'cash4';

export type OfficialDrawStyle = {
  source: 'florida' | 'newyork' | 'georgia';
  threeField: OfficialThreeField;
  fourField: OfficialFourField;
  /** Short UI labels (English keys resolved in i18n). */
  threeLabelKey: 'godResultsPick3' | 'godResultsNumbers' | 'godResultsCash3';
  fourLabelKey: 'godResultsPick4' | 'godResultsWin4' | 'godResultsCash4';
};

export type OfficialDrawNativeStored = {
  pick3?: string | null;
  pick4?: string | null;
  numbers?: string | null;
  win4?: string | null;
  cash3?: string | null;
  cash4?: string | null;
};

export type OfficialDrawNativeFields = OfficialDrawNativeStored & {
  first?: string | null;
  second?: string | null;
  third?: string | null;
};

export type ResolvedOfficialReading = {
  /** 3-digit official game (Pick 3 / Numbers / Cash 3). */
  three: string | null;
  /** 4-digit official game (Pick 4 / Win 4 / Cash 4). */
  four: string | null;
  /** Alias kept for centena / existing corrida helpers. */
  pick3: string | null;
  pick4: string | null;
};

function digitsOnly(raw: unknown): string {
  return String(raw ?? '').replace(/\D/g, '');
}

function padDigits(raw: unknown, length: number): string {
  const digits = digitsOnly(raw);
  if (!digits) return '';
  return digits.padStart(length, '0').slice(-length);
}

export function normalizeLotteryStyleSource(
  source: LotteryStyleSource | null | undefined,
): 'florida' | 'newyork' | 'georgia' {
  const v = String(source ?? 'florida').trim().toLowerCase();
  if (v === 'newyork' || v === 'ny') return 'newyork';
  if (v === 'georgia' || v === 'ga') return 'georgia';
  return 'florida';
}

export function officialDrawStyleForSource(
  source: LotteryStyleSource | null | undefined,
): OfficialDrawStyle {
  const normalized = normalizeLotteryStyleSource(source);
  if (normalized === 'newyork') {
    return {
      source: 'newyork',
      threeField: 'numbers',
      fourField: 'win4',
      threeLabelKey: 'godResultsNumbers',
      fourLabelKey: 'godResultsWin4',
    };
  }
  if (normalized === 'georgia') {
    return {
      source: 'georgia',
      threeField: 'cash3',
      fourField: 'cash4',
      threeLabelKey: 'godResultsCash3',
      fourLabelKey: 'godResultsCash4',
    };
  }
  return {
    source: 'florida',
    threeField: 'pick3',
    fourField: 'pick4',
    threeLabelKey: 'godResultsPick3',
    fourLabelKey: 'godResultsPick4',
  };
}

/**
 * Resolve ### + #### from mixed/legacy docs, preferring the active source's native fields.
 */
export function resolveOfficialReading(
  source: LotteryStyleSource | null | undefined,
  fields: OfficialDrawNativeFields,
): ResolvedOfficialReading {
  const style = officialDrawStyleForSource(source);
  const first = padDigits(fields.first, 2);
  const second = padDigits(fields.second, 2);
  const third = padDigits(fields.third, 2);

  const candidates3 = [
    padDigits(fields[style.threeField], 3),
    padDigits(fields.pick3, 3),
    padDigits(fields.numbers, 3),
    padDigits(fields.cash3, 3),
  ].filter((v) => v.length === 3);

  let three = candidates3.find((v) => !first || v.endsWith(first)) ?? '';
  if (!three && candidates3[0]) three = '';

  const candidates4 = [
    padDigits(fields[style.fourField], 4),
    padDigits(fields.pick4, 4),
    padDigits(fields.win4, 4),
    padDigits(fields.cash4, 4),
    second.length === 2 && third.length === 2 ? `${second}${third}` : '',
  ].filter((v) => v.length === 4);

  const four = candidates4[0] ?? '';

  return {
    three: three.length === 3 ? three : null,
    four: four.length === 4 ? four : null,
    pick3: three.length === 3 ? three : null,
    pick4: four.length === 4 ? four : null,
  };
}

/** Payload fragment to persist for the active lottery style (native fields only). */
export function nativeOfficialDrawPayload(
  source: LotteryStyleSource | null | undefined,
  threeRaw: string,
  fourRaw: string,
): OfficialDrawNativeStored {
  const style = officialDrawStyleForSource(source);
  const three = padDigits(threeRaw, 3);
  const four = padDigits(fourRaw, 4);
  const out: OfficialDrawNativeStored = {
    pick3: null,
    pick4: null,
    numbers: null,
    win4: null,
    cash3: null,
    cash4: null,
  };
  if (three.length === 3) out[style.threeField] = three;
  if (four.length === 4) out[style.fourField] = four;
  return out;
}

/** House `picks` / `draws` sub-object for the active style. */
export function nativeOfficialPicksBag(
  source: LotteryStyleSource | null | undefined,
  threeRaw: string | null | undefined,
  fourRaw: string | null | undefined,
  extra: Record<string, unknown> = {},
): Record<string, unknown> {
  const style = officialDrawStyleForSource(source);
  const three = threeRaw != null ? padDigits(threeRaw, 3) : '';
  const four = fourRaw != null ? padDigits(fourRaw, 4) : '';
  const bag: Record<string, unknown> = { ...extra };
  if (style.source === 'newyork') {
    if (three) bag.numbers = three;
    if (four) bag.win4 = four;
  } else if (style.source === 'georgia') {
    if (three) bag.cash3 = three;
    if (four) bag.cash4 = four;
  } else {
    if (three) bag.pick3 = three;
    if (four) bag.pick4 = four;
  }
  return bag;
}
